// Entry file
/* eslint-disable @atlaskit/editor/no-re-export */

import { getConversationStarters } from '@atlaskit/rovo-agent-components/get-conversation-starters';
import type {
	ConversationStarter,
	StaticAgentConversationStarter,
} from '@atlaskit/rovo-agent-components/ui/AgentConversationStarters';
import type { usePublish } from '@atlaskit/rovo-triggers/main';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
// Testing data until we start to load it from client.
export { ChatAgent, CommsCrafterAgent, JobWriterAgent, A11yMateAgent } from './mock-agents';

export type EditorAgent = Pick<
	Agent,
	'id' | 'name' | 'description' | 'creator' | 'visibility' | 'icon'
> & {
	// Additional fields we can populate on client side
	// color: string;
	// iconInitials: string;
	conversationStarters: (ConversationStarter | StaticAgentConversationStarter)[];
	creatorType: Agent['creator_type'];
	externalConfigReference?: Agent['external_config_reference'];
	identityAccountId?: Agent['identity_account_id'];
	isDefault: Agent['is_default'];
	// These are from Agent, just renamed to camel case.
	namedId: Agent['named_id'];

	systemPromptTemplate: Agent['system_prompt_template'];

	// Populated when creating agent config items in UserInputCommandPaletteWithLogic.
} & (
		| { channelId: string; publish: ReturnType<typeof usePublish> }
		| { channelId?: never; publish?: never }
	);

/**
 * Pulls out the necessary fields from the raw agent data received to create an EditorAgent.
 * We can use this to populate additional fields like color, iconInitials etc.
 *
 * @param agent
 */
export const getEditorAgent = (agent: Agent): EditorAgent => {
	const {
		id,
		name,
		description,
		named_id,
		creator_type,
		system_prompt_template,
		creator,
		visibility,
		is_default,
		identity_account_id,
		icon,
	} = agent;

	return {
		id,
		name,
		description,
		namedId: named_id,
		creatorType: creator_type,
		systemPromptTemplate: system_prompt_template,
		creator: creator ?? undefined,
		visibility,
		isDefault: is_default,
		identityAccountId: identity_account_id,
		icon: icon,
		conversationStarters: getConversationStarters({
			isAgentDefault: is_default,
			userDefinedConversationStarters: agent.user_defined_conversation_starters?.map((starter) => {
				return {
					message: starter,
					type: 'user-defined',
				};
			}),
		}).combinedConversationStarters,
		externalConfigReference: agent.external_config_reference,
	};
};
